import React, {
  FC,
  useContext,
  useState,
  useMemo,
  useCallback,
  Dispatch,
  SetStateAction,
} from "react";
import { useHistory } from "react-router-dom";
import clsx from "clsx";

import { Context, subjectsPath } from "../../../managerStore";
import { RegistrationSubject } from "../../../state";
import { ModalContext } from "../../../libs/ModalProvider";
import Confirm from "../../../molecules/Confirm";
import { ReactComponent as DeleteIcon } from "../../../assets/icon/delete.svg";

import SubjectSelectModal from "./SubjectSelectModal";
import styles from "./SubjectRegistration.module.scss";

type Props = {
  calling: boolean;
  teacherId: number;
  registrationSubjects: RegistrationSubject[];
  teacherName: string;
  setRegistrationSubjects: Dispatch<SetStateAction<RegistrationSubject[]>>;
  submitTeacherSubjects: (
    teacherId: number,
    subjects: RegistrationSubject[],
  ) => void;
  deleteTeacherSubject: (teacherId: number, id: number) => void;
  submitSubject: (name: string) => void;
  updateSubject: (name: string, id: number) => void;
  deleteSubject: (id: number) => void;
};

const SubjectRegistration: FC<Props> = ({
  calling,
  teacherId,
  registrationSubjects,
  teacherName,
  setRegistrationSubjects,
  submitTeacherSubjects,
  deleteTeacherSubject,
  submitSubject,
  updateSubject,
  deleteSubject,
}) => {
  const { state } = useContext(Context);
  const ctx = useContext(ModalContext);
  const history = useHistory();
  const [isOpenSubjectSelectModal, setIsOpenSubjectSelectModal] =
    useState(false);

  const mergedClasses = [...state.classes, ...state.sub_classes];

  const selectSubjects = useCallback(
    (ids: number[]) => {
      setRegistrationSubjects(prev => {
        const prevMap = new Map(prev.map(s => [s.subject_id, s]));
        return ids.map(
          id => prevMap.get(id) ?? { subject_id: id, klass_ids: [] },
        );
      });
    },
    [setRegistrationSubjects],
  );

  const selectClass = useCallback(
    (subject_id: number, klass_id: number) => {
      setRegistrationSubjects(prev =>
        prev.map(subject => {
          if (subject.subject_id !== subject_id) {
            return subject;
          }

          const hasKlass = subject.klass_ids.includes(klass_id);

          if (hasKlass) {
            return {
              ...subject,
              klass_ids: subject.klass_ids.filter(id => id !== klass_id),
            };
          } else {
            return {
              ...subject,
              klass_ids: [...subject.klass_ids, klass_id],
            };
          }
        }),
      );
    },
    [setRegistrationSubjects],
  );

  const handleDeleteSubject = useCallback(
    (subjectId: number) => {
      const targetTeacherSubjectId =
        state.teacher_subjects_by_teacher.subjects.find(
          subject => subject.subject_id === subjectId,
        )?.teacher_subject_id;
      targetTeacherSubjectId &&
        deleteTeacherSubject(teacherId, targetTeacherSubjectId);
      setRegistrationSubjects(prev => {
        return prev.filter(p => p.subject_id !== subjectId);
      });
    },
    [
      state.teacher_subjects_by_teacher.subjects,
      teacherId,
      setRegistrationSubjects,
      deleteTeacherSubject,
    ],
  );

  const getSubjectById = useCallback(
    (id: number) => {
      return state.subjects.find(subject => subject.id === id);
    },
    [state.subjects],
  );

  const subjectNamesWithKlassChanges = useMemo(() => {
    const regMap = new Map<number, Set<number>>(
      registrationSubjects.map(({ subject_id, klass_ids }) => [
        subject_id,
        new Set(klass_ids),
      ]),
    );

    const hasRemovals = (current: Set<number>, next: Set<number>) =>
      Array.from(current).some(id => !next.has(id));

    return state.teacher_subjects_by_teacher.subjects
      .filter(({ subject_id, klasses }) => {
        const current = new Set(klasses.map(k => k.klass_id));
        const registered = regMap.get(subject_id) ?? new Set<number>();
        return hasRemovals(current, registered);
      })
      .map(s => s.subject_name);
  }, [registrationSubjects, state.teacher_subjects_by_teacher.subjects]);

  const isRegistrationDisabled =
    registrationSubjects.length === 0 ||
    registrationSubjects.some(data => data.klass_ids.length === 0) ||
    calling;

  return (
    <div className={styles.wrapper}>
      {isOpenSubjectSelectModal && (
        <SubjectSelectModal
          calling={calling}
          registrationSubjects={registrationSubjects}
          onClose={() => setIsOpenSubjectSelectModal(false)}
          selectSubjects={selectSubjects}
          submitSubject={submitSubject}
          updateSubject={updateSubject}
          deleteSubject={deleteSubject}
        />
      )}
      <div className={styles.inner}>
        <p className={styles.title}>教科を登録</p>
        <p className={styles.note}>
          ※担当しているすべてのクラス・教科を登録してください。
        </p>
        <p className={styles.teacherName}>{teacherName}</p>
        <div className={styles.selectArea}>
          <button
            className={styles.subjectSelectButton}
            onClick={() => setIsOpenSubjectSelectModal(true)}
          >
            担当教科を選択
          </button>
        </div>
        <p className={styles.heading}>教科のクラスを登録</p>
        {state.classes.length === 0 && (
          <p className={styles.note}>※クラスが未登録です。</p>
        )}
        {registrationSubjects.length > 0 ? (
          <div className={styles.subjectClassGroup}>
            {registrationSubjects.map(data => (
              <div key={data.subject_id} className={styles.subjectClassBlock}>
                <div className={styles.header}>
                  <p className={styles.label}>
                    {getSubjectById(data.subject_id)?.name}
                  </p>
                  <button
                    className={styles.deleteButton}
                    onClick={() => {
                      const hasSubjectId =
                        state.teacher_subjects_by_teacher.subjects.some(
                          subject => subject.subject_id === data.subject_id,
                        );
                      if (hasSubjectId) {
                        ctx.actions.showModal(
                          <Confirm
                            calling={calling}
                            closeModal={() => {
                              ctx.actions.closeModal();
                            }}
                            variant="danger"
                            content={
                              <>
                                削除しますか？
                                <br />
                                対象のBOXは募集終了（生徒が投稿できない状態）になります。すでに提出されているストックは削除されずそのまま残ります。
                                <br />
                                <span className={styles.warningText}>
                                  ※ストックの受付を再開することはできませんのでご注意ください。
                                </span>
                              </>
                            }
                            callback={() => {
                              handleDeleteSubject(data.subject_id);
                              ctx.actions.closeModal();
                            }}
                          />,
                        );
                      } else {
                        setRegistrationSubjects(prev => {
                          return prev.filter(
                            p => p.subject_id !== data.subject_id,
                          );
                        });
                      }
                    }}
                  >
                    <DeleteIcon className={styles.deleteIcon} />
                  </button>
                </div>
                {mergedClasses.length > 0 && (
                  <ul className={styles.classList}>
                    {mergedClasses.map(klass => (
                      <li
                        key={klass.id}
                        className={clsx(
                          styles.item,
                          data.klass_ids.includes(klass.id) &&
                            styles.isSelected,
                        )}
                        onClick={() => selectClass(data.subject_id, klass.id)}
                      >
                        {klass.full_name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className={styles.note}>※担当教科を選択してください。</p>
        )}
        <button
          className={styles.registrationButton}
          onClick={() => {
            if (subjectNamesWithKlassChanges.length > 0) {
              ctx.actions.showModal(
                <Confirm
                  calling={calling}
                  closeModal={() => {
                    ctx.actions.closeModal();
                  }}
                  variant="danger"
                  content={
                    <>
                      {subjectNamesWithKlassChanges.join(",")}
                      のクラスが変更になります。
                      <br />
                      変更対象のクラスのBOXは募集終了（生徒が投稿できない状態）になります。すでに提出されているストックは削除されずそのまま残ります。
                      <br />
                      <span className={styles.warningText}>
                        ※ストックの受付を再開することはできませんのでご注意ください。
                      </span>
                    </>
                  }
                  callback={() => {
                    submitTeacherSubjects(teacherId, registrationSubjects);
                    history.push(subjectsPath);
                  }}
                />,
              );
            } else {
              submitTeacherSubjects(teacherId, registrationSubjects);
              history.push(subjectsPath);
            }
          }}
          disabled={isRegistrationDisabled}
        >
          登録
        </button>
      </div>
    </div>
  );
};

export default SubjectRegistration;
